/**
 * Routage des pièces d'identité par (personne × type × face).
 *
 * Directive François (retours CGIA 20/07, FX-187-XA) : « Il faut dissocier les
 * pièces d'identité titulaire et co-titulaire dans le listing avec RECTO et
 * VERSO. Il doit prendre le certificat de demande d'immatriculation, identifier
 * le titulaire et de là chercher la pièce d'identité et le permis de conduire
 * (recto et verso), et utiliser ces champs pour le peuplement. »
 *
 * Historiquement, les pièces d'identité vivaient dans 3 slots par TYPE de
 * document (`piece_identite`, `piece_identite_hebergeur`,
 * `piece_identite_cotitulaire`), un seul document par slot, routés par des
 * heuristiques d'ordre d'arrivée (overflow multi-CNI). Conséquences réelles :
 * verso du co-titulaire égaré en slot hébergeur (FX-187-XA), CNI du
 * co-titulaire prise pour celle du titulaire (DY-565-KJ, CY-517-VW), pièce de
 * l'hébergeur rattachée à l'acheteur (DM-724-VQ, GP-226-HQ).
 *
 * Ce module remplace ces heuristiques par un routage DÉTERMINISTE recalculé à
 * chaque arrivée de pièce, indépendant de l'ordre d'ingestion OCR :
 *  1. Les RÔLES sont identifiés depuis les pièces de référence — Cerfa 13750
 *     (titulaire + co-titulaire), croisé avec la cession (acheteur) et le
 *     mandat (mandant) ; l'hébergeur vient de l'attestation d'hébergement.
 *  2. Chaque pièce d'identité (CNI / permis) est rattachée à sa personne par
 *     matching nom/prénom — nom d'usage inclus (une personne mariée apparaît
 *     sous son nom d'usage sur la cession et son nom de naissance sur sa CNI).
 *  3. La FACE (recto = côté identité/photo, verso) est lue depuis les flags du
 *     modèle (`face_identite_presente`), et chaque (personne × type × face)
 *     occupe son propre slot `CgDocType`.
 *
 * Module PUR (aucun effet de bord, aucun accès store) → testable directement.
 */

import { CgDocType } from '../ocr/ocrResponseTypes';
import type { UploadedDocResult } from '../store/useFormCGIAStore';

/**
 * La pièce porte-t-elle sa FACE D'IDENTITÉ (photo + nom/prénom) ?
 * Signal explicite du modèle (`face_identite_presente`) prioritaire ; à défaut,
 * présence d'au moins un champ d'identité fort. (Déplacé depuis le store — le
 * store le ré-exporte pour ses consommateurs historiques.)
 */
export const docHasIdentityFace = (doc?: UploadedDocResult): boolean => {
  const fields = doc?.ocrResponse?.fields as Record<string, { value?: unknown }> | undefined;
  if (!fields) return false;
  if (fields['face_identite_presente']?.value === false) return false;
  return Boolean(
    fields['nom']?.value ||
      fields['prenom']?.value ||
      fields['numero_permis']?.value ||
      fields['numero_document']?.value,
  );
};

export type IdentityPerson = 'titulaire' | 'cotitulaire' | 'hebergeur';
export type IdentityFace = 'recto' | 'verso';
export type IdentityKind = 'cni' | 'permis';

/** Une pièce d'identité du pool, avec son éventuel rôle imposé par la zone de dépôt. */
export interface IdentityPoolEntry {
  doc: UploadedDocResult;
  /**
   * Rôle imposé par la PROVENANCE du dépôt (zone dédiée co-titulaire /
   * hébergeur). Information plus fiable que tout matching : on ne la remet
   * pas en cause.
   */
  pinnedPerson?: IdentityPerson;
}

/** Tous les slots d'identité (personne × type × face). */
export const IDENTITY_SLOT_TYPES: readonly CgDocType[] = [
  'piece_identite',
  'piece_identite_verso',
  'piece_identite_cotitulaire',
  'piece_identite_cotitulaire_verso',
  'piece_identite_hebergeur',
  'piece_identite_hebergeur_verso',
  'permis_conduire',
  'permis_conduire_verso',
  'permis_conduire_cotitulaire',
  'permis_conduire_cotitulaire_verso',
];

export const isIdentitySlotType = (docType: string): boolean =>
  (IDENTITY_SLOT_TYPES as readonly string[]).includes(docType);

/** Type « nature de pièce » (cni/permis) d'un docType d'identité. */
export const identityKindOf = (docType: CgDocType): IdentityKind | null => {
  if (docType.startsWith('piece_identite')) return 'cni';
  if (docType.startsWith('permis_conduire')) return 'permis';
  return null;
};

/** Rôle éventuellement encodé par le docType (zones de dépôt dédiées). */
export const identityPersonOf = (docType: CgDocType): IdentityPerson => {
  if (docType.includes('cotitulaire')) return 'cotitulaire';
  if (docType.includes('hebergeur')) return 'hebergeur';
  return 'titulaire';
};

const SLOT_BY_ASSIGNMENT: Record<IdentityKind, Record<IdentityPerson, Record<IdentityFace, CgDocType | null>>> = {
  cni: {
    titulaire: { recto: 'piece_identite', verso: 'piece_identite_verso' },
    cotitulaire: { recto: 'piece_identite_cotitulaire', verso: 'piece_identite_cotitulaire_verso' },
    hebergeur: { recto: 'piece_identite_hebergeur', verso: 'piece_identite_hebergeur_verso' },
  },
  permis: {
    titulaire: { recto: 'permis_conduire', verso: 'permis_conduire_verso' },
    cotitulaire: { recto: 'permis_conduire_cotitulaire', verso: 'permis_conduire_cotitulaire_verso' },
    // Pas de slot permis hébergeur (le permis de l'hébergeur n'est pas une
    // pièce du dossier CTVO) : une telle pièce reste non assignée.
    hebergeur: { recto: null, verso: null },
  },
};

export const identitySlotFor = (kind: IdentityKind, person: IdentityPerson, face: IdentityFace): CgDocType | null =>
  SLOT_BY_ASSIGNMENT[kind][person][face];

/**
 * Séparateur de la clé de pool. Absent des URLs Firebase (encodées), donc sans
 * ambiguïté sur la partie URL de la clé.
 */
export const POOL_KEY_SEP = '#kind:';

/**
 * Clé d'une pièce DANS LE POOL : (URL Firebase × nature de pièce).
 *
 * Un scan contenant à la fois la CNI et le permis d'une personne arrive en DEUX
 * résultats qui partagent leur URL — ils occupent deux slots différents, et
 * l'URL seule ne peut donc pas les départager (ni dans le routage, ni à la
 * persistance de `routed_doc_type`).
 */
export const identityPoolKey = (imageUrl: string, kind: IdentityKind): string => `${imageUrl}${POOL_KEY_SEP}${kind}`;

const normName = (v: unknown): string =>
  String(v ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const docFieldStr = (doc: UploadedDocResult | undefined, key: string): string | null => {
  const v = (doc?.ocrResponse?.fields as Record<string, { value?: unknown }> | undefined)?.[key]?.value;
  return typeof v === 'string' && v.trim().length > 0 ? v : null;
};

interface NameRef {
  nom: string | null;
  prenom: string | null;
}

export interface PersonRefs {
  titulaire: NameRef[];
  cotitulaire: NameRef[];
  hebergeur: NameRef[];
}

/**
 * Construit les références de nom par personne depuis les pièces de référence.
 * Méthodo François : le Cerfa 13750 est le récapitulatif de la demande — c'est
 * LUI qui dit qui est titulaire et qui est co-titulaire ; la cession et le
 * mandat corroborent.
 */
export const buildPersonRefs = (docs: Partial<Record<string, UploadedDocResult>>): PersonRefs => {
  const compact = (refs: NameRef[]): NameRef[] => refs.filter((r) => r.nom || r.prenom);
  return {
    titulaire: compact([
      {
        nom: docFieldStr(docs.cerfa_demande_immat, 'titulaire_nom'),
        prenom: docFieldStr(docs.cerfa_demande_immat, 'titulaire_prenom'),
      },
      {
        nom: docFieldStr(docs.cerfa_cession, 'acheteur_nom'),
        prenom: docFieldStr(docs.cerfa_cession, 'acheteur_prenom'),
      },
      { nom: docFieldStr(docs.mandat, 'mandant_nom'), prenom: docFieldStr(docs.mandat, 'mandant_prenom') },
    ]),
    cotitulaire: compact([
      {
        nom: docFieldStr(docs.cerfa_demande_immat, 'cotitulaire_nom'),
        prenom: docFieldStr(docs.cerfa_demande_immat, 'cotitulaire_prenom'),
      },
    ]),
    hebergeur: compact([
      {
        nom: docFieldStr(docs.attestation_hebergement, 'hebergeur_nom'),
        prenom: docFieldStr(docs.attestation_hebergement, 'hebergeur_prenom'),
      },
    ]),
  };
};

/**
 * Score d'une pièce d'identité contre une référence de nom.
 * Prénom prioritaire (×2 — hébergement intrafamilial : nom de famille commun),
 * et le nom de référence peut être le nom de naissance OU le nom d'usage de la
 * pièce (FX-187-XA : cession « ROGER », CNI « MOUSSIER » nom d'usage « ROGER »).
 */
const scoreDocVsRef = (doc: UploadedDocResult, ref: NameRef): number => {
  const nomOk = Boolean(
    ref.nom &&
      [docFieldStr(doc, 'nom'), docFieldStr(doc, 'nom_usage')].some((v) => v && normName(ref.nom) === normName(v)),
  );
  const prenomOk = Boolean(
    ref.prenom && docFieldStr(doc, 'prenom') && normName(ref.prenom) === normName(docFieldStr(doc, 'prenom')),
  );
  return (prenomOk ? 2 : 0) + (nomOk ? 1 : 0);
};

const scoreDocVsPerson = (doc: UploadedDocResult, refs: NameRef[]): number =>
  refs.length === 0 ? 0 : Math.max(0, ...refs.map((r) => scoreDocVsRef(doc, r)));

/** Face d'une pièce d'identité : recto = face porteuse de l'identité (photo). */
export const identityFaceOf = (doc: UploadedDocResult): IdentityFace => (docHasIdentityFace(doc) ? 'recto' : 'verso');

const PERSON_ORDER: readonly IdentityPerson[] = ['titulaire', 'cotitulaire', 'hebergeur'];

/**
 * Ordre de préférence pour un VERSO MUET (aucun nom, aucun n° de document) qui
 * doit rejoindre l'un des rectos en attente.
 *
 * Le co-titulaire passe AVANT le titulaire, à rebours de `PERSON_ORDER`. Ce
 * n'est pas arbitraire : le formulaire RÉCLAME explicitement le verso du
 * co-titulaire (les deux faces sont exigées par l'ANTS, cf. règle
 * `piece_identite_cotitulaire_incomplete`) et ne réclame JAMAIS celui du
 * titulaire — dont l'identité est déjà établie par son recto et son permis.
 * Un verso déposé sans nom répond donc, selon toute vraisemblance, à la ligne
 * « Pièce d'identité du co-titulaire — VERSO » affichée en attente à l'écran.
 *
 * Cas fondateur EA-035-SV (retour Luc 2026-07-30) : une CNI ancien format porte
 * sa MRZ au RECTO, son verso ne porte donc ni nom ni numéro. Le verso de Mme
 * LEGRAND partait au titulaire par simple effet d'ordre, laissant sa propre
 * ligne « VERSO » en attente alors que la pièce était bien au dossier.
 */
const VERSO_FALLBACK_ORDER: readonly IdentityPerson[] = ['cotitulaire', 'titulaire', 'hebergeur'];

const confidenceOf = (entry: IdentityPoolEntry): number => entry.doc.ocrResponse?.docTypeMatchConfidence ?? 0;

export interface IdentityRoutingResult {
  /**
   * Slot assigné par FICHIER (clé = URL Firebase) — pour l'affichage : libellé
   * d'une pièce reçue, choix manuel de personne. Un fichier multi-pièces (CNI +
   * permis dans le même scan) n'y porte qu'un seul de ses deux slots :
   * `slotByPoolKey` fait foi pour tout ce qui doit les distinguer.
   */
  slotByUrl: Record<string, CgDocType>;
  /** Slot assigné par pièce du pool : clé = `identityPoolKey(url, nature)`. */
  slotByPoolKey: Record<string, CgDocType>;
  /** Documents par slot, docType réécrit au slot assigné. */
  slots: Partial<Record<CgDocType, UploadedDocResult>>;
  /**
   * Pièces assignées SANS certitude sur la personne : un verso muet a été placé
   * par simple repli alors que PLUSIEURS personnes du dossier étaient également
   * éligibles. L'assignation reste faite (il faut bien afficher la pièce), mais
   * elle relève du choix par défaut, pas d'une preuve.
   *
   * Cas fondateur EA-035-SV : une CNI ANCIEN format porte sa MRZ au RECTO — son
   * verso ne comporte ni nom, ni n° de document, seulement l'adresse et les
   * dates de validité. Quand le couple a fait ses deux cartes le même mois, plus
   * aucun signal ne dit à qui appartient un verso : c'est indécidable par OCR,
   * et seule la main du déposant peut trancher (cf. `pinnedPerson`).
   */
  ambiguousUrls: string[];
}

/**
 * Route TOUTES les pièces d'identité du pool vers leurs slots
 * (personne × type × face). Recalculé intégralement à chaque appel : le
 * résultat ne dépend que du contenu du pool et des pièces de référence,
 * jamais de l'ordre d'arrivée.
 *
 * Règles d'assignation, dans l'ordre :
 *  1. Rôle imposé par la zone de dépôt (`pinnedPerson`) — jamais remis en cause.
 *  2. Matching nom/prénom/nom d'usage contre les références (13750 prioritaire,
 *     cession/mandat pour le titulaire, attestation pour l'hébergeur).
 *  3. Pièces sans match (verso muet, références pas encore extraites) :
 *     - un RECTO va à la première personne (titulaire → hébergeur) dont le slot
 *       recto est libre (préserve la sémantique historique de l'overflow) ;
 *     - un VERSO est apparié à la personne (même type de pièce) qui a un recto
 *       assigné mais pas encore de verso (titulaire → cotitulaire → hébergeur),
 *       sinon au titulaire.
 *  4. Collision sur un même slot : la pièce à la meilleure confiance de type
 *     gagne ; un recto délogé retente les personnes RÉELLEMENT AU DOSSIER
 *     (`ROLES_REELS`), sinon reste non assigné (il demeure visible dans la
 *     liste des fichiers reçus).
 */
export const routeIdentityDocs = (
  pool: readonly IdentityPoolEntry[],
  refDocs: Partial<Record<string, UploadedDocResult>>,
): IdentityRoutingResult => {
  const refs = buildPersonRefs(refDocs);
  const slots: Partial<Record<CgDocType, UploadedDocResult>> = {};
  const slotByPoolKey: Record<string, CgDocType> = {};
  const ambiguousUrls: string[] = [];

  // ── Rôles RÉELLEMENT présents au dossier ──
  //
  // Un rôle « existe » s'il est nommé par une pièce de référence (co-titulaire
  // du 13750, attestation d'hébergement), ou si une pièce a été déposée dans sa
  // zone dédiée (`pinnedPerson`). Le titulaire existe toujours.
  //
  // C'est la garde centrale du routage : AUCUNE pièce ne peut être rangée dans
  // le slot d'une personne que le dossier ne déclare pas. Sans elle, toute
  // pièce d'identité en trop (2e exemplaire de la pièce de l'hébergeur, 2e
  // permis de l'acquéreur, pièce d'un proche portant le même nom de famille ou
  // le nom d'usage de l'acquéreur) débordait sur le slot CO-TITULAIRE simple-
  // ment parce qu'il était libre — et, le bloc co-titulaire du formulaire étant
  // allumé par la seule présence de `cotitulaire_nom` (cf. COTITULAIRE_FIELD_
  // REMAP et deriveReviewCotitulaire), CGIA INVENTAIT un co-titulaire que
  // l'agence n'avait aucun moyen d'effacer.
  //
  // Retours François du 17/09 : FK-371-EZ (l'hébergeur RODRIGUEZ Michel, déposé
  // en deux fichiers, passe co-titulaire), GF-387-AM (Mme BEN HAYOUN, nom
  // d'usage CAMPOCASSO comme l'acquéreur, passe co-titulaire), EC-634-JV (les
  // deux permis de SEGONNE Pascal le font titulaire ET co-titulaire) — dans les
  // trois cas le Cerfa 13750 est VIERGE de co-titulaire.
  const hasPinned = (person: IdentityPerson): boolean => pool.some((e) => e.pinnedPerson === person);
  const cotitulaireDeclare = refs.cotitulaire.length > 0 || hasPinned('cotitulaire');
  // C'est l'ATTESTATION d'hébergement qui matérialise le rôle d'hébergeur (même
  // règle que la checklist agence et la grille /review). À son arrivée, le
  // routage est recalculé (`IDENTITY_REF_DOC_TYPES`), donc un dossier
  // d'hébergement retrouve son slot dès que la pièce est analysée.
  const hebergeurReel = Boolean(refDocs.attestation_hebergement) || hasPinned('hebergeur');
  const ROLES_REELS: readonly IdentityPerson[] = PERSON_ORDER.filter((p) =>
    p === 'cotitulaire' ? cotitulaireDeclare : p === 'hebergeur' ? hebergeurReel : true,
  );

  interface Candidate {
    entry: IdentityPoolEntry;
    kind: IdentityKind;
    face: IdentityFace;
    person: IdentityPerson | null;
  }

  const candidates: Candidate[] = pool
    .map((entry) => {
      const kind = identityKindOf(entry.doc.docType);
      if (!kind) return null;
      return { entry, kind, face: identityFaceOf(entry.doc), person: null } as Candidate;
    })
    .filter((c): c is Candidate => c !== null);

  // ── 1+2 : rôle imposé, puis matching nominal ──
  candidates.forEach((c) => {
    if (c.entry.pinnedPerson) {
      c.person = c.entry.pinnedPerson;
      return;
    }
    let best: IdentityPerson | null = null;
    let bestScore = 0;
    PERSON_ORDER.forEach((person) => {
      const s = scoreDocVsPerson(c.entry.doc, refs[person]);
      if (s > bestScore) {
        best = person;
        bestScore = s;
      }
    });
    c.person = best;
  });

  const urlOf = (c: Candidate): string => c.entry.doc.firebaseUrls[0] ?? '';
  /**
   * Identifiant d'une pièce DANS LE POOL. Un fichier multi-pièces (CNI + permis
   * dans le même scan) produit DEUX entrées qui partagent leur URL Firebase :
   * indexer par URL seule faisait que la seconde effaçait la première, si bien
   * que la CNI de l'acquéreur était comptée comme « déjà assignée » (elle
   * sautait les replis) et que son slot ne partait jamais en base — elle
   * revenait alors `identity_unrouted` dans la review, donc sans alimenter
   * l'identité de personne. La clé est donc (URL × nature de pièce), comme
   * l'éviction du pool côté store.
   */
  const keyOf = (c: Candidate): string => identityPoolKey(urlOf(c), c.kind);

  const tryAssign = (c: Candidate, person: IdentityPerson): boolean => {
    const slot = identitySlotFor(c.kind, person, c.face);
    if (!slot) return false;
    const existing = slots[slot];
    if (!existing) {
      slots[slot] = { ...c.entry.doc, docType: slot };
      slotByPoolKey[keyOf(c)] = slot;
      return true;
    }
    return false;
  };

  const assignWithCollision = (c: Candidate, person: IdentityPerson): boolean => {
    const slot = identitySlotFor(c.kind, person, c.face);
    if (!slot) return false;
    const existingKey = Object.entries(slotByPoolKey).find(([, s]) => s === slot)?.[0];
    const existingCandidate = candidates.find((other) => keyOf(other) === existingKey);
    if (!slots[slot] || !existingCandidate) return tryAssign(c, person);
    // Collision : la meilleure confiance de type garde le slot.
    if (confidenceOf(c.entry) > confidenceOf(existingCandidate.entry)) {
      delete slotByPoolKey[keyOf(existingCandidate)];
      slots[slot] = { ...c.entry.doc, docType: slot };
      slotByPoolKey[keyOf(c)] = slot;
      // Le délogé retente les personnes RÉELLEMENT au dossier (recto uniquement
      // — un verso délogé resterait ambigu, il reste non assigné).
      if (existingCandidate.face === 'recto') {
        for (const p of ROLES_REELS) {
          if (p !== person && tryAssign(existingCandidate, p)) break;
        }
      }
      return true;
    }
    return false;
  };

  // ── Assignation des pièces à personne identifiée (pinned d'abord : la
  // provenance du dépôt prime sur tout matching en cas de collision) ──
  const identified = candidates.filter((c) => c.person !== null);
  identified.sort((a, b) => Number(Boolean(b.entry.pinnedPerson)) - Number(Boolean(a.entry.pinnedPerson)));
  identified.forEach((c) => {
    if (!c.person) return;
    if (!assignWithCollision(c, c.person)) {
      // Slot occupé par mieux classé : un recto retente les autres personnes
      // RÉELLEMENT au dossier. Faute de quoi il reste non assigné — visible
      // dans « Autres pièces reçues » côté agence, et marqué
      // `identity_unrouted` côté review, donc sans alimenter les champs
      // d'identité de qui que ce soit.
      if (c.face === 'recto') {
        for (const p of ROLES_REELS) {
          if (p !== c.person && tryAssign(c, p)) return;
        }
      }
    }
  });

  // ── 3 : pièces sans personne identifiée ──
  //
  // Ordre de REPLI des VERSOS MUETS (aucun nom, aucun n° de document
  // exploitable). Contrairement à un recto — qui porte un nom et finit donc
  // identifié —, un verso muet ne sera JAMAIS re-identifié : le slot qu'on lui
  // donne ici est définitif, il doit donc désigner une personne qui EXISTE
  // vraiment au dossier. Sans cette garde, sur EA-035-SV (reg 11910) un verso
  // muet partait en `piece_identite_hebergeur_verso` alors qu'AUCUNE attestation
  // d'hébergement n'était déposée et qu'un co-titulaire LEGRAND était déclaré au
  // 13750 ; ses dates écrasaient au passage celles du permis du titulaire
  // (retour Luc 2026-07-30). C'est `ROLES_REELS`, désormais partagé avec les
  // replis de collision (cf. plus haut).
  const FALLBACK_PERSONS = ROLES_REELS;

  const unassigned = candidates.filter((c) => slotByPoolKey[keyOf(c)] === undefined);
  unassigned
    .filter((c) => c.face === 'recto')
    .forEach((c) => {
      // Un recto inconnu se gare dans le premier slot libre pour ne pas écraser
      // les pièces déjà placées, et le re-routage le replace dès qu'une pièce de
      // référence nomme sa personne (FX-187-XA : Mickael passe en co-titulaire à
      // l'arrivée du 13750) — le routage étant recalculé intégralement, ce
      // garage n'est jamais nécessaire à sa conservation.
      //
      // Les rôles candidats sont ceux qui EXISTENT au dossier : garer la pièce
      // d'un tiers en slot hébergeur alors qu'aucune attestation n'a été
      // déposée inventait un hébergeur, et le point d'attention « pièce
      // étrangère au dossier » se tait précisément sur ce slot faute de nom de
      // référence à comparer (cf. piece_identite_etrangere_au_dossier). La
      // pièce reste donc sans slot : visible dans « Autres pièces reçues »,
      // marquée `identity_unrouted` côté review.
      for (const p of ROLES_REELS) {
        if (tryAssign(c, p)) return;
      }
    });
  const docNumberOf = (doc: UploadedDocResult): string | null => {
    const raw = docFieldStr(doc, 'numero_document') ?? docFieldStr(doc, 'numero_permis');
    return raw ? raw.replace(/[^a-z0-9]/gi, '').toUpperCase() : null;
  };
  // Le n° de document du verso (bande MRZ) est celui du recto — c'est LE
  // signal d'appariement fiable quand le verso ne porte pas de nom lisible.
  // Match par préfixe (≥ 6 caractères communs) : l'OCR tronque parfois d'un
  // caractère (FX-187-XA : recto « GHHV6LBG4 », verso « GHHV6LBG45 »).
  const docNumbersMatch = (a: string | null, b: string | null): boolean => {
    if (!a || !b) return false;
    const minLen = Math.min(a.length, b.length);
    if (minLen < 6) return false;
    return a.slice(0, minLen) === b.slice(0, minLen);
  };

  unassigned
    .filter((c) => c.face === 'verso' && slotByPoolKey[keyOf(c)] === undefined)
    .forEach((c) => {
      // 1. Appariement par n° de document avec un recto déjà assigné.
      //
      // ⚠️ L'appariement n'est retenu que s'il désigne UNE SEULE personne. Les
      // CNI d'un couple sont souvent délivrées le même jour au même endroit et
      // partagent un long préfixe (EA-035-SV : 170328101487 et 170328101522, 8
      // caractères communs) : si l'OCR tronque le numéro, le match par préfixe
      // peut viser les DEUX rectos. Prendre le premier de PERSON_ORDER
      // rattacherait alors le verso au titulaire par pur effet d'ordre —
      // exactement l'erreur silencieuse qu'on cherche à éliminer. En cas
      // d'ambiguïté, on laisse les replis suivants décider.
      const versoNumber = docNumberOf(c.entry.doc);
      if (versoNumber) {
        const matches = PERSON_ORDER.filter((p) => {
          const rectoSlot = identitySlotFor(c.kind, p, 'recto');
          const recto = rectoSlot ? slots[rectoSlot] : undefined;
          return Boolean(recto && docNumbersMatch(versoNumber, docNumberOf(recto)));
        });
        if (matches.length === 1 && tryAssign(c, matches[0])) return;
      }
      // 2. Un verso muet rejoint la personne (même type de pièce) qui a un
      // recto mais pas encore de verso — en ignorant les rectos déjà complets
      // (`est_recto_seul === false` : les deux faces sont dans le même fichier,
      // cette personne n'attend pas de verso).
      //
      // Quand PLUSIEURS personnes attendent ainsi un verso, aucune ne l'emporte
      // sur preuve : on prend la première de PERSON_ORDER pour que la pièce
      // s'affiche, mais on marque l'assignation comme AMBIGUË — le déposant
      // pourra la corriger d'un clic (le choix manuel arrive ensuite en
      // `pinnedPerson`, qui n'est jamais remis en cause).
      const enAttenteDeVerso = VERSO_FALLBACK_ORDER.filter((p) => {
        const rectoSlot = identitySlotFor(c.kind, p, 'recto');
        const versoSlot = identitySlotFor(c.kind, p, 'verso');
        const recto = rectoSlot ? slots[rectoSlot] : undefined;
        const rectoComplet =
          recto &&
          (recto.ocrResponse?.fields as Record<string, { value?: unknown }> | undefined)?.['est_recto_seul']?.value ===
            false;
        return Boolean(recto && !rectoComplet && versoSlot && !slots[versoSlot]);
      });
      for (const p of enAttenteDeVerso) {
        if (tryAssign(c, p)) {
          if (enAttenteDeVerso.length > 1) ambiguousUrls.push(urlOf(c));
          return;
        }
      }
      // Aucun recto orphelin : verso au titulaire (comportement historique du
      // slot unique), sinon aux rôles réellement présents au dossier. Un verso
      // qui ne trouve aucune personne reste NON assigné : il apparaît dans
      // « Autres pièces reçues » plutôt que rattaché à un tiers inventé.
      for (const p of FALLBACK_PERSONS) {
        if (tryAssign(c, p)) {
          if (FALLBACK_PERSONS.length > 1) ambiguousUrls.push(urlOf(c));
          return;
        }
      }
    });

  // `slotByUrl` reste indexé par URL pour les consommateurs d'affichage (libellé
  // d'un fichier reçu, choix manuel de personne) ; sur un fichier multi-pièces
  // il ne peut porter qu'un slot, d'où `slotByPoolKey` pour la persistance.
  const slotByUrl: Record<string, CgDocType> = {};
  Object.entries(slotByPoolKey).forEach(([key, slot]) => {
    slotByUrl[key.slice(0, key.lastIndexOf(POOL_KEY_SEP))] = slot;
  });

  return { slotByUrl, slotByPoolKey, slots, ambiguousUrls };
};
